import * as React from "react";
import { cn } from "@/lib/utils";
import type { InvoiceListSnapshot, InvoiceLabels, InvoiceStatus } from "./types";
import { isOverdue } from "./utils";

interface Props {
  snapshot: InvoiceListSnapshot;
  labels: InvoiceLabels;
  activeStatus: InvoiceStatus | "all" | "overdue";
  onChange: (next: InvoiceStatus | "all" | "overdue") => void;
}

export function InvoiceTabChips({ snapshot, labels, activeStatus, onChange }: Props) {
  const counts = React.useMemo(() => {
    const today = new Date();
    return {
      all: snapshot.rows.length,
      draft: snapshot.totals.draftCount,
      issued: snapshot.totals.issuedCount,
      paid: snapshot.totals.paidCount,
      failed: snapshot.totals.failedCount,
      overdue: snapshot.rows.filter((r) => isOverdue(r, today)).length,
    };
  }, [snapshot]);

  const tabs: Array<{ key: InvoiceStatus | "all" | "overdue"; label: string; count: number; tone?: "warning" | "danger" }> = [
    { key: "all", label: labels.tabs.all, count: counts.all },
    { key: "draft", label: labels.status.draft, count: counts.draft },
    { key: "issued", label: labels.status.issued, count: counts.issued },
    { key: "paid", label: labels.status.paid, count: counts.paid },
    { key: "failed", label: labels.status.failed, count: counts.failed, tone: "danger" },
    { key: "overdue", label: labels.tabs.overdue, count: counts.overdue, tone: "warning" },
  ];

  return (
    <div role="tablist" aria-label={labels.filters.status} className="flex flex-wrap items-center gap-2">
      {tabs.map((t) => {
        const active = activeStatus === t.key;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.key)}
            className={cn(
              "h-10 px-3 inline-flex items-center gap-2 rounded-full border text-sm font-semibold transition-colors biz-focus-ring biz-motion-safe",
              active
                ? "bg-biz-primary text-biz-primary-foreground border-biz-primary"
                : "bg-biz-surface-elevated text-biz-foreground-muted border-biz-border hover:text-biz-foreground hover:bg-biz-muted",
              !active && t.tone === "danger" && t.count > 0 && "text-biz-danger border-biz-danger-border",
              !active && t.tone === "warning" && t.count > 0 && "text-biz-warning border-biz-warning-border",
            )}
          >
            <span>{t.label}</span>
            <span
              className={cn(
                "font-mono-num text-[11px] px-1.5 h-5 inline-flex items-center justify-center rounded-full",
                active ? "bg-biz-primary-foreground/20" : "bg-biz-muted",
              )}
            >
              {t.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}
